import { DomainList, DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteHotel, useGetHotels } from "@/hooks/hotels";
import { useGetRegions } from "@/hooks/regions";
import { T } from "@/i18n";
import { Hotel } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BedDouble, Hotel as HotelIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(hotels)/hotels")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetHotels();

  return (
    <DomainList
      domain="hotels"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="hotels.title" /> <HotelIcon width={16} height={16} />
            </>
          ),
          href: "/hotels",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "hotels.fields.name",
          },
          {
            header: "hotels.fields.regionId",
          },
          {
            header: "hotels.fields.locality",
          },
          {
            header: "hotels.fields.group",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/hotels/new"
      renderItem={(hotel, index, isTable) => (
        <HotelCard hotel={hotel} key={index} isTable={isTable} />
      )}
    />
  );
}

function HotelCard({ hotel, isTable }: { hotel: Hotel; isTable: boolean }) {
  const { mutateAsync } = useDeleteHotel();
  const deleteHotel = () => mutateAsync(hotel.id.toString());
  const { data: regions, isLoading } = useGetRegions({
    enabled: typeof hotel.regionId === "number",
  });
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="hotels"
      title={hotel.name}
      description={
        typeof hotel.regionId !== "number" ? (
          "-"
        ) : (
          <>
            {isLoading || !regions ? (
              <Skeleton />
            ) : (
              (regions.find((r) => r.id === hotel.regionId?.toString())?.name ??
              "-")
            )}
          </>
        )
      }
      viewLink={(children, key) => (
        <Link key={key} to="/hotels/$id" params={{ id: hotel.id.toString() }}>
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/hotels/$id/edit"
          params={{ id: hotel.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          label: "hotelRooms.title",
          icon: () => <BedDouble />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/hotel-rooms/$id"
              params={{ id: hotel.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        {
          label: t("hotels.fields.locality"),
          value: hotel.locality || "-",
        },
        {
          label: t("hotels.fields.group"),
          value: hotel.group || "-",
        },
      ]}
      onDelete={deleteHotel}
    />
  );
}
